import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

type TenantCtx = {
  tenant_name?: string;
  tenant_domain?: string;
};

type AnalyticsParams = Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private tenantContext: TenantCtx = {};
  private initialized = false;

  constructor(private router: Router) {
    (window as any).dataLayer = (window as any).dataLayer || [];
  }

  /** measurementId is unused now (GTM owns GA), keep signature to avoid app changes */
  init(_: string, tenant: TenantCtx) {
    this.tenantContext = {
      tenant_name: tenant.tenant_name || window.location.hostname,
      tenant_domain: tenant.tenant_domain || window.location.hostname,
    };

    if (this.initialized) return;
    this.initialized = true;

    // Route changes => page_view (SPA)
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.pageView(e.urlAfterRedirects));
  }

  pageView(path?: string) {
    (window as any).dataLayer.push({
      event: 'page_view',
      page_location: window.location.href,
      page_path: path || window.location.pathname,
      page_title: document.title,
      ...this.tenantContext,
    });
  }

  /**
   * Main generic event sender.
   * Supports both:
   * - normal custom events
   * - GA4 ecommerce events when `items` is provided
   */
  event(name: string, params: AnalyticsParams = {}) {
    const dl = (window as any).dataLayer;

    const { items, currency, value, transaction_id, ...rest } = params;

    if (Array.isArray(items)) {
      // Clear previous ecommerce object before sending a new one
      dl.push({ ecommerce: null });

      const ecommerce: any = { items };

      if (currency != null) ecommerce.currency = currency;
      if (value != null) ecommerce.value = value;

      dl.push({
        event: name,
        ...rest,
        ...(transaction_id != null ? { transaction_id } : {}),
        ...this.tenantContext,
        ecommerce,
      });
      return;
    }

    dl.push({
      event: name,
      ...params,
      ...this.tenantContext,
    });
  }

  /**
   * Alias for consistency in feature components.
   * This lets components call `trackEvent(...)` while keeping
   * `event(...)` as the single implementation.
   */
  trackEvent(name: string, params: AnalyticsParams = {}) {
    this.event(name, params);
  }

  setTenant(tenant: TenantCtx) {
    this.tenantContext = { ...this.tenantContext, ...tenant };
  }
}
